import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useAuth from "../useAuth";
import useWebotServer from "../useWebotServer";
import { createLookupBatcher } from "./contactLookupBatcher";
import { contactIdForLookup } from "./contactLookupKey";
import { contactLookupStore } from "./contactLookupStore";
import { rowToContactSummary } from "./contactSummary";
import { CONTACTS_LOOKUP_MAX_IDS } from "../../@types/webotServer";
import type { ContactSummary } from "./contactSummary";
import type { ContactsLookupResponse } from "../../@types/webotServer";

/**
 * "WHO IS THIS PHONE" — for every screen that shows a phone and wants a name beside it.
 *
 * ```tsx
 * const { lookup } = useContactsLookup(calls.map((call) => call.callerPhoneNumber));
 * // …
 * const contact = lookup(call.callerPhoneNumber);
 * const label = contact?.contactFullName || call.callerPhoneNumber;
 * ```
 *
 * ── What `lookup` returns ───────────────────────────────────────────────────────────
 *
 *   undefined   not answered yet (or the request failed)  → show the phone; claim nothing
 *   null        answered: not in the caller's book        → may offer "add to contacts"
 *   summary     the contact — a PROJECTION, see `ContactSummary`
 *
 * ── What it replaces ────────────────────────────────────────────────────────────────
 *
 * `useLegacyContactsBook().getAllContacts()` followed by `.find()` on the caller's whole book. Calling that
 * getter is what downloads the book; do not add a new call to it. A screen that needs a
 * contact's name needs this hook. A card or dialog about ONE contact needs
 * `useContactByPhone` (the live document) — never the projection, and above all never an
 * edit form opened on one.
 *
 * ── How it stays cheap ──────────────────────────────────────────────────────────────
 *
 *  - One module store (`contactLookupStore`): two surfaces showing the same phone share one
 *    answer. Call the hook wherever the phones are known — the table AND its side panel —
 *    without threading a prop between them.
 *  - Everything asked for in a tick leaves as chunked requests (`contactLookupBatcher`), at
 *    most three at a time.
 *  - A list should still pass ALL its phones in one call rather than mount a hook per row:
 *    every answer re-renders every mounted reader.
 *
 * Phones may be in any spelling; `contactIdForLookup` turns each into the contact id.
 */

/** The latest `contactsLookup` — `useWebotServer()` rebuilds its functions on every render. */
let fetchRows: ((contactIds: string[]) => Promise<ContactsLookupResponse>) | null = null;

const batcher = createLookupBatcher({
  chunkSize: CONTACTS_LOOKUP_MAX_IDS,
  concurrency: 3,
  send: async (chunk) => {
    // Captured when the request LEAVES: the token it travels with is the account's that is
    // signed in now, so this is the session its answer belongs to.
    const session = contactLookupStore.session();
    if (!fetchRows) {
      contactLookupStore.release(session, chunk);
      return;
    }
    try {
      const response = await fetchRows(chunk);
      contactLookupStore.record(session, chunk, response.contacts.map(rowToContactSummary));
    } catch (error) {
      // Released, never recorded as "not in the book": that is a claim screens act on.
      contactLookupStore.release(session, chunk);
      // eslint-disable-next-line no-console
      console.error("useContactsLookup: failed to look up contacts", error);
    }
  }
});

/** Binds the store to the signed-in account; a switch also drops whatever has not left yet. */
const bindAccount = (account: string | null): void => {
  const before = contactLookupStore.session();
  contactLookupStore.bindAccount(account);
  if (contactLookupStore.session() !== before) batcher.clear();
};

export type ContactsLookup = {
  /** See the header for the three answers. Accepts the phone in any spelling. */
  lookup: (phone: string | null | undefined) => ContactSummary | null | undefined;
  /**
   * An answer is still ON ITS WAY for at least one of the phones. False once every request
   * has come back — answered or FAILED — so a screen that waits on this cannot hang on a
   * request that will never answer. Wait on this when going ahead without the answer is
   * acceptable (a name that stays a phone number).
   */
  isResolving: boolean;
  /**
   * Every phone has a definite answer: a contact, or "not in the book". Stays false after a
   * failed request. Gate on this when the screen ACTS on the answers — "skip the ones I
   * already have" must not run over phones nobody answered for.
   */
  allAnswered: boolean;
};

export default function useContactsLookup(
  phones: readonly (string | null | undefined)[],
  options: { enabled?: boolean } = {}
): ContactsLookup {
  const { contactsLookup } = useWebotServer();
  const { user } = useAuth();
  const account = (user?.email as string | undefined) ?? null;
  // `enabled: false` stops this reader ASKING (a row scrolled out of view, a closed dialog).
  // It still READS: an answer another reader already fetched is as true here as there.
  const enabled = (options.enabled ?? true) && Boolean(account);

  // During render, and idempotent: the store must belong to THIS account before anything
  // below reads it, or the first frame after a sign-in shows the previous user's names.
  bindAccount(account);

  const fetcher = useRef(contactsLookup);
  fetcher.current = contactsLookup;

  // Re-render when ANY reader's answer lands — the point of a shared store. A subscriber
  // set plus a counter, as in `useCrmAssignments`: this app is on React 17, which has no
  // `useSyncExternalStore`.
  const [version, setVersion] = useState(0);
  useEffect(() => contactLookupStore.subscribe(() => setVersion((value) => value + 1)), []);

  // The ids stand for the array: stable across the re-renders an answer causes, and equal
  // for two arrays that name the same phones in the same order.
  const idsKey = useMemo(() => {
    const ids = new Set<string>();
    for (const phone of phones) {
      const id = contactIdForLookup(phone);
      if (id) ids.add(id);
    }
    return [...ids].join("|");
  }, [phones]);

  // Moves when the store is invalidated (an import, a bulk edit): every mounted reader
  // re-asks about what it is showing, and nothing else is fetched.
  const epoch = contactLookupStore.epoch();

  // One key for "what to ask, as whom, and since which invalidation" — a change in any of
  // them is a reason to ask again. Null when there is nothing to ask.
  const askKey = enabled && idsKey ? `${account} ${epoch} ${idsKey}` : null;
  // The key the effect below last ran for. Until it has run for the current one, the phones
  // count as "about to be asked" — otherwise the render BEFORE the request leaves would
  // report that nothing is on its way.
  const askedKey = useRef<string | null>(null);

  useEffect(() => {
    askedKey.current = askKey;
    if (!askKey) return;
    fetchRows = (contactIds) => fetcher.current(contactIds);
    const claimed = contactLookupStore.claim(idsKey.split("|"));
    if (claimed.length > 0) batcher.request(claimed);
  }, [askKey, idsKey]);

  const lookup = useCallback(
    (phone: string | null | undefined): ContactSummary | null | undefined => {
      if (!account) return undefined;
      const id = contactIdForLookup(phone);
      return id ? contactLookupStore.read(id) : undefined;
    },
    // `version` is what makes this a NEW function when an answer lands, so a memoised
    // child that takes `lookup` as a prop re-renders with it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [account, version]
  );

  const unanswered = idsKey ? idsKey.split("|").filter((id) => contactLookupStore.read(id) === undefined) : [];
  const allAnswered = unanswered.length === 0;
  // On its way = this reader is about to ask (its effect has not run for these phones yet),
  // or a request is out. An unanswered phone that is neither was asked and FAILED.
  const isResolving =
    askKey !== null && !allAnswered && (askedKey.current !== askKey || unanswered.some((id) => contactLookupStore.isInFlight(id)));

  return { lookup, isResolving, allAnswered };
}

/**
 * The same answer for ONE phone — a chip, a card header, a single cell outside any list.
 *
 * Inside a list, prefer one `useContactsLookup` at the list and pass `lookup` down.
 */
export function useContactLookup(
  phone: string | null | undefined,
  options: { enabled?: boolean } = {}
): ContactSummary | null | undefined {
  const phones = useMemo(() => [phone], [phone]);
  return useContactsLookup(phones, options).lookup(phone);
}
